<template>
  <booking-block
    class="booking-block--traveler"
    :valid="!validForm"
    :title="header"
    :subTitle="subTitle"
    :trackingTitle="standardTitle"
    :editMode="editMode"
    @save="saveAndClose"
    @edit="$emit('edit')"
    ref="bookingblock"
  >
    <template slot="edit">
      <div class="input-fields">
        <div class="input-fields__content">
          <selector
            v-if="value.type !== 'K'"
            :value="value.type"
            @input="(type) => updateData({ type })"
            :options="genderOptions"
            icon-type="drop-down"
            class="booking-field booking-field--gender"
          ></selector>
          <input-field
            :name="translations.input_firstname"
            :value="value.firstName"
            @input="(inp) => updateData({ firstName: inp })"
            :icon-type="
              validForm && validForm.firstName ? '' : this.checkmarkIcon
            "
            class="booking-field booking-field--firstname"
          ></input-field>
          <input-field
            :name="translations.input_lastname"
            :value="value.lastName"
            @input="(inp) => updateData({ lastName: inp })"
            :icon-type="
              validForm && validForm.lastName ? '' : this.checkmarkIcon
            "
            class="booking-field booking-field--lastname"
          ></input-field>
        </div>
        <msg-block type="error" v-if="errors.firstName || errors.lastName">
          <p v-if="errors.firstName"><b v-text="errors.firstName[0]"></b></p>
          <p v-if="errors.lastName"><b v-text="errors.lastName[0]"></b></p>
        </msg-block>
      </div>
      <div class="input-fields">
        <div class="input-fields__header">
          <h3 v-text="translations.birthday_header"></h3>
        </div>
        <div class="input-fields__content">
          <selector
            v-model="day"
            :options="days"
            :icon-type="day == '-1' ? 'drop-down' : this.checkmarkIcon"
            class="booking-field booking-field--day"
          ></selector>
          <selector
            v-model="month"
            :options="months"
            :icon-type="month == '-1' ? 'drop-down' : this.checkmarkIcon"
            class="booking-field booking-field--month"
          ></selector>
          <selector
            v-model="year"
            :options="years"
            class="booking-field booking-field--year"
            :icon-type="year == '-1' ? 'drop-down' : this.checkmarkIcon"
          ></selector>
        </div>
        <msg-block type="error" v-if="errors.dateOfBirth">
          <p v-if="errors.dateOfBirth">
            <b v-text="errors.dateOfBirth[0]"></b>
          </p>
        </msg-block>
      </div>
      <div class="input-fields">
        <div class="input-fields__header">
          <h3 v-text="translations.nationality_header"></h3>
        </div>
        <div class="input-fields__content">
          <selector
            :options="nationality"
            :value="value.nationality"
            @input="(val) => updateData({ nationality: val })"
            :icon-type="validForm && validForm.nationality ? 'drop-down' : this.checkmarkIcon"
            class="booking-field booking-field--country"
          ></selector>
          <selector
            :options="nationalityOrigin"
            :value="value.residenceCountryCode"
            @input="(val) => updateData({ residenceCountryCode: val })"
            :icon-type="validForm && validForm.residenceCountryCode ? 'drop-down' : this.checkmarkIcon"
            class="booking-field booking-field--citizenship"
          ></selector>
        </div>
        <msg-block
          type="error"
          v-if="errors.nationality || errors.residenceCountryCode"
        >
          <p v-if="errors.nationality">
            <b v-text="errors.nationality[0]"></b>
          </p>
          <p v-if="errors.residenceCountryCode">
            <b v-text="errors.residenceCountryCode[0]"></b>
          </p>
        </msg-block>
      </div>
    </template>
    <template slot="view">
      <table class="table">
        <tbody>
          <tr v-if="value.type !== 'K' && selectedGender">
            <td v-text="selectedGender"></td>
            <td></td>
          </tr>
          <tr>
            <td v-text="translations.input_firstname"></td>
            <td v-text="value.firstName"></td>
          </tr>
          <tr>
            <td v-text="translations.input_lastname"></td>
            <td v-text="value.lastName"></td>
          </tr>
          <tr>
            <td v-text="translations.birthday_header"></td>
            <td>
              {{ birthdayDateMoment ? birthdayDateMoment.format("LL") : "" }}
            </td>
          </tr>
          <tr>
            <td v-text="translations.nationality_ro_field"></td>
            <td v-text="selectedNationality"></td>
          </tr>
          <tr>
            <td v-text="translations.nationality_origin_ro_field"></td>
            <td v-text="selectedNationalityOrigin"></td>
          </tr>
        </tbody>
      </table>
    </template>
    <div class="booking-block__footer">
      <div class="booking-block__footer__attention">
        <div class="icon">
           <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
            <path d="M20.57,24.8c0,.19-.15,.34-.34,.34H11.77c-.19,0-.34-.15-.34-.34v-1.61c0-.19,.15-.34,.34-.34h1.95v-8h-1.95c-.19,0-.34-.15-.34-.34v-1.61c0-.19,.15-.34,.34-.34h6.18c.19,0,.34,.15,.34,.34v9.95h1.95c.19,0,.34,.15,.34,.34v1.61ZM16,5.66c1.29,0,2.34,1.05,2.34,2.34s-1.05,2.34-2.34,2.34-2.34-1.05-2.34-2.34,1.05-2.34,2.34-2.34Zm0-5.66C7.16,0,0,7.17,0,16s7.16,16,16,16,16-7.17,16-16S24.84,0,16,0Z"/>
          </svg>
        </div>
        <p v-text="translations.notice"></p>
      </div>
    </div>
  </booking-block>
</template>

<script>
import InputField from "../input-field.vue";
import Selector from "../selector.vue";
import Moment from "moment";
import Validate from "validate.js";
import MsgBlock from "../msg-block.vue";
import BookingBlock from "./booking-block.vue";

export default {
  props: {
    number: { type: Number, default: 1 },
    editMode: { type: Boolean, default: true },
    value: {
      type: Object,
      default() {
        return {
          firstName: "",
          lastName: "",
          dateOfBirth: "",
          nationality: "DK",
          residenceCountryCode: "DK",
          type: "H",
        };
      },
    },
  },
  data() {
    return {
      checkmarkIcon: "checkmark",
      translations: APP_CONFIG.TRANSLATION.BOOKING_TRAVELER,
      errors: {},
      year: "-1",
      month: "-1",
      day: "-1",
      showErrors: false,
    };
  },
  mounted() {
    this.$emit("valid", !this.validForm ? true : false);
    if (this.editMode) {
      this.readDateData();
    }
  },
  watch: {
    "value.dateOfBirth"() {
      this.readDateData();
    },
    validForm() {
      if (this.showErrors) {
        this.updateErrors();
      }

      this.$emit("valid", !this.validForm ? true : false);
    },
    birthdayDate() {
      this.updateData({
        dateOfBirth: this.birthdayDate,
      });
    },
    showErrors() {
      if (this.showErrors) {
        this.showValidationErrors();
      } else {
        this.hideValidationErrors();
      }
    },
    editMode() {
      if (this.editMode) {
        this.readDateData();
      }
    },
  },
  methods: {
    saveAndClose() {
      if (this.validForm === null) {
        this.hideValidationErrors();
        this.$nextTick(() => {
          this.$refs.bookingblock.close();
        });
      } else {
        this.showValidationErrors();
      }
    },
    showValidationErrors() {
      this.showErrors = true;
      this.updateErrors();
    },
    hideValidationErrors() {
      this.showErrors = false;
      this.errors = {};
    },
    updateErrors() {
      this.errors = Object.assign({}, this.validForm);
    },
    updateData(updated) {
      this.$emit("input", Object.assign({}, this.value, updated));
    },
    readDateData() {
      if (!this.value || !this.value.dateOfBirth) {
        return;
      }

      let date = Moment(this.value.dateOfBirth, "YYYY-MM-DD");
      if (date) {
        this.day = date.format("DD").toString();
        this.month = date.format("MM").toString();
        this.year = date.format("YYYY").toString();
      }
    },
  },
  computed: {
    validForm() {
      let errors = Validate(
        this.value,
        {
          firstName: {
            presence: {
              allowEmpty: false,
              message: this.translations.ERRORS.firstName,
            },
          },
          lastName: {
            presence: {
              allowEmpty: false,
              message: this.translations.ERRORS.lastName,
            },
          },
          dateOfBirth: {
            presence: {
              allowEmpty: false,
              message: this.translations.ERRORS.dateOfBirth,
            },
          },
          nationality: {
            presence: {
              allowEmpty: false,
              message: this.translations.ERRORS.nationality,
            },
          },
          residenceCountryCode: {
            presence: {
              allowEmpty: false,
              message: this.translations.ERRORS.residenceCountryCode,
            },
          },
        },
        {
          fullMessages: false,
        }
      );

      return errors ? errors : null;
    },
    birthdayDate() {
      if (
        parseInt(this.year) > 0 &&
        parseInt(this.month) > 0 &&
        parseInt(this.day) > 0
      ) {
        return this.year + "-" + this.month + "-" + this.day;
      }

      return "";
    },
    birthdayDateMoment() {
      return this.value.dateOfBirth
        ? Moment(this.value.dateOfBirth, "YYYY-MM-DD")
        : null;
    },
    days() {
      let days = [{ text: this.translations.date_day, value: "-1" }];
      for (let i = 1; i <= 31; i++) {
        let day = i.toString();
        if (i < 10) {
          day = "0" + day;
        }

        days.push({
          text: i.toString(),
          value: day,
        });
      }

      return days;
    },
    months() {
      return [{ text: this.translations.date_month, value: "-1" }].concat(
        Moment.months().map((val, key) => {
          let month = key + 1;
          if (month < 10) {
            month = "0" + month.toString();
          }

          return {
            value: month.toString(),
            text: val,
          };
        })
      );
    },
    years() {
      let year = Moment().year();
      year =
        this.value.type !== "K" ? year - APP_CONFIG.MAX_AGE_CHILDREN : year + 1;
      let yearsToGoBack = this.value.type !== "K" ? 100 : APP_CONFIG.MAX_AGE_CHILDREN + 2;

      let years = [{ text: this.translations.date_year, value: "-1" }];
      for (let i = 0; i < yearsToGoBack; i++) {
        years.push((year - i).toString());
      }

      return years;
    },
    genderOptions() {
      let prefix_select = this.translations.prefix_select;
      let list = [];

      for (let i in prefix_select) {
        list.push({
          value: i,
          text: prefix_select[i],
        });
      }

      return list;
    },
    header() {
      let str = "";

      if (this.value.firstName) {
        str = this.value.firstName;

        if (this.value.lastName) {
          str += " " + this.value.lastName;
        }

        return str;
      }

      str = this.standardTitle;
      return str;
    },
    subTitle() {
      if (this.validForm === null) {
        return this.standardTitle;
      } else if (this.validForm) {
        return this.translations.sub_header;
      }

      return "";
    },
    standardTitle() {
        let str =
          this.value.type !== "K"
            ? this.translations.traveler_adult
            : this.translations.traveler_children;
        return str + " " + this.number;
    },
    nationalityOrigin() {
      return [
        {
          text: this.translations.nationality_origin,
          value: " ",
        },
      ].concat(
        this.$store.state.countries.map((country) => {
          return {
            text: country.nationality,
            value: country.code,
          };
        })
      );
    },
    nationality() {
      return [
        {
          text: this.translations.nationality_selector,
          value: " ",
        },
      ].concat(
        this.$store.state.countries.map((country) => {
          return {
            text: country.name,
            value: country.code,
          };
        })
      );
    },
    selectedNationality() {
      if (this.value.nationality) {
        let nationality = this.nationality.filter(
          (nationality) => nationality.value == this.value.nationality
        );
        if (nationality.length) {
          return nationality[0].text;
        }
      }

      return "";
    },
    selectedNationalityOrigin() {
      if (this.value.residenceCountryCode) {
        let nationalityOrigin = this.nationalityOrigin.filter(
          (nationalityOrigin) =>
            nationalityOrigin.value == this.value.residenceCountryCode
        );

        if (nationalityOrigin.length) {
          return nationalityOrigin[0].text;
        }
      }

      return "";
    },
    selectedGender() {
      if (this.value.type) {
        let selectedGender = this.genderOptions.filter(
          (gender) => gender.value == this.value.type
        );

        if (selectedGender.length) {
          return selectedGender[0].text;
        }
      }

      return "";
    },
  },
  components: {
    InputField,
    Selector,
    MsgBlock,
    BookingBlock,
  },
};
</script>

